// CreatePlayerModal — shared modal that both admin and agent shells use
// to onboard a new player. Mirrors CreateSubAgentModal in look and
// feel so the operator sees a consistent experience.
//
// mode='agent'
//   POSTs to /admin/agents/<currentAgentId>/players (the agent self-
//   service path). agent_id is implicit — the route :id is the caller.
//
// mode='admin'
//   POSTs to /admin/users with optional agent_id picked from a
//   dropdown of all agents (or null = platform-direct).
//
// Required fields: username, email, phone, password.
// Optional fields: first_name, last_name, dob, gender, language.
// PHASE R3: legacy free-text address inputs removed; structured geo
//   captured via LocationCapture and submitted as the new geo bag
//   (state_id/district_id/area_id/pincode_id + lat/lng + nation).
//   Geo is OPTIONAL here because the admin/agent operator may not
//   know the player's location at onboarding — the player can fill it
//   in themselves on first login. The G1 hard-block on /auth/register
//   does NOT fire here because admin/agent-created accounts get an
//   agent_id directly (admin selects, agent is implicit) and the
//   server short-circuits the geo cascade.
// All optional fields skip the wire if blank.

import { useEffect, useMemo, useState } from 'react';
import Icon from '../../components/Icon';
import { adminApi } from '../lib/adminApi';
import { ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import LocationCapture, { type GeoBag, EMPTY_GEO_BAG } from '../../components/LocationCapture';
import PlayerLimitsFields, { type PlayerLimits, limitsPayload } from './PlayerLimitsFields';

type CreatedUser = { id: number; username: string; email: string; phone: string };
type AgentOpt = { id: number; username: string; level?: number };

// Language picker — endonyms stay literal so a user fluent in (say)
// Tamil can spot it regardless of UI language. The display labels are
// the languages' own names, not translations of "English".
const LANGUAGES: Array<[string, string]> = [
  ['en', 'English'], ['hi', 'हिन्दी'], ['bn', 'বাংলা'], ['mr', 'मराठी'],
  ['ta', 'தமிழ்'],   ['te', 'తెలుగు'], ['gu', 'ગુજરાતી'], ['ur', 'اردو'],
  ['kn', 'ಕನ್ನಡ'],   ['or', 'ଓଡ଼ିଆ'], ['pa', 'ਪੰਜਾਬੀ'], ['ml', 'മലയാളം'],
];

// Gender options — `value` is the wire enum, `labelKey` resolves at
// render time via t(). Order preserved from the original literal array.
const GENDER_OPTS: Array<{ value: string; labelKey: string }> = [
  { value: '',           labelKey: 'agent.createPlayer.gender.unspecified' },
  { value: 'male',       labelKey: 'agent.createPlayer.gender.male' },
  { value: 'female',     labelKey: 'agent.createPlayer.gender.female' },
  { value: 'other',      labelKey: 'agent.createPlayer.gender.other' },
  { value: 'prefer_not', labelKey: 'agent.createPlayer.gender.preferNot' },
];

export default function CreatePlayerModal({
  mode,
  callerAgentId,
  initialAgentId,
  onClose,
  onCreated,
}: {
  mode: 'admin' | 'agent';
  callerAgentId?: number;             // required when mode='agent'
  initialAgentId?: number;            // admin only — preselect owning agent
  onClose: () => void;
  onCreated: (user: CreatedUser) => void;
}) {
  const t = useT();
  // Required identity
  const [username, setUsername] = useState('');
  const [email, setEmail]       = useState('');
  const [phone, setPhone]       = useState('');
  const [password, setPassword] = useState('');

  // Phase D3 — first_name, last_name, birthdate are MANDATORY at the
  // API. The frontend enforces by including them in the `valid` check
  // below and surfaces a hint if the operator leaves any blank. Other
  // identity fields (gender, language) remain optional.
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [birthdate, setBirthdate]   = useState('');
  const [gender, setGender]         = useState('');
  const [language, setLanguage]     = useState('en');
  // Structured geo (Phase R3). Starts empty; operator fills in if
  // they know the player's location — otherwise we POST without geo
  // and the server falls back to attribution via the explicit
  // agent_id (mode='admin' picker, or implicit caller for mode='agent').
  const [geo, setGeo]               = useState<GeoBag>(EMPTY_GEO_BAG);
  const [limits, setLimits]         = useState<PlayerLimits>({});

  // Admin-only: choose owning agent
  const [agentId, setAgentId] = useState<number | ''>(initialAgentId ?? '');
  const [agents, setAgents]   = useState<AgentOpt[]>([]);

  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  // Load agent picker for admin mode.
  useEffect(() => {
    if (mode !== 'admin') return;
    (async () => {
      try {
        const r = await adminApi.get<{ rows: AgentOpt[] }>('/admin/hierarchy/agents?limit=200');
        setAgents(r.rows || []);
      } catch { /* leave list empty; admin can still create platform-direct */ }
    })();
  }, [mode]);

  const valid = useMemo(() => {
    return (
      /^[A-Za-z0-9_]{3,30}$/.test(username.trim()) &&
      /^\S+@\S+\.\S+$/.test(email.trim()) &&
      /^\+?\d{7,15}$/.test(phoneE164(phone)) &&
      password.length >= 8 &&
      /[A-Za-z]/.test(password) && /\d/.test(password) &&
      // Phase D3 — identity fields are now mandatory.
      firstName.trim().length >= 2 &&
      lastName.trim().length  >= 2 &&
      /^\d{4}-\d{2}-\d{2}$/.test(birthdate)
    );
  }, [username, email, phone, password, firstName, lastName, birthdate]);

  function phoneE164(p: string) {
    const t = p.trim();
    if (t.startsWith('+')) return t;
    return '+91' + t.replace(/\D/g, '');
  }

  async function submit() {
    if (!valid || busy) return;
    setErr(null); setBusy(true);
    try {
      const profile: Record<string, string> = {};
      const stash = (k: string, v: string) => { if (v.trim()) profile[k] = v.trim(); };
      stash('first_name',    firstName);
      stash('last_name',     lastName);
      stash('birthdate',     birthdate);
      stash('gender',        gender);
      stash('language',      language);

      const body: Record<string, unknown> = {
        username: username.trim(),
        email:    email.trim().toLowerCase(),
        phone:    phoneE164(phone),
        password,
        ...profile,
      };

      // Structured geo — forward only fields the operator actually
      // populated. The admin/agent controller's createPlayer Zod
      // schema accepts the same geo bag the public /auth/register
      // schema does, so the wire format is identical.
      if (geo.latitude    != null) body.latitude    = geo.latitude;
      if (geo.longitude   != null) body.longitude   = geo.longitude;
      if (geo.state_id    != null) body.state_id    = geo.state_id;
      if (geo.district_id != null) body.district_id = geo.district_id;
      if (geo.area_id     != null) body.area_id     = geo.area_id;
      if (geo.pincode_id  != null) body.pincode_id  = geo.pincode_id;
      if (geo.nation)              body.nation      = geo.nation;

      const lp = limitsPayload(limits);
      if (lp) body.limits = lp;

      let resp: { user: CreatedUser };
      if (mode === 'agent') {
        if (!callerAgentId) throw new Error(t('agent.createPlayer.error.missingAgentId'));
        resp = await adminApi.post<{ user: CreatedUser }>(
          `/admin/agents/${callerAgentId}/players`, body,
        );
      } else {
        if (agentId !== '') (body as Record<string, unknown>).agent_id = agentId;
        resp = await adminApi.post<{ user: CreatedUser }>('/admin/users', body);
      }
      onCreated(resp.user);
    } catch (e) {
      setErr((e as ApiError).message || t('agent.createPlayer.error.createFailed'));
    } finally {
      setBusy(false);
    }
  }

  // Lock background scroll while modal open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="fixed inset-0 z-[60] bg-black/45 grid place-items-start sm:place-items-center p-3 sm:p-4 overflow-y-auto" role="dialog" aria-modal="true">
      <div
        className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800
                   shadow-2xl max-w-lg w-full flex flex-col overscroll-contain my-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="grid place-items-center w-9 h-9 rounded-xl
                             bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100
                             dark:bg-cyan-400/15 dark:text-cyan-300 dark:ring-cyan-400/30">
              <Icon name="person_add" size={18} filled />
            </span>
            <div className="min-w-0">
              <div className="text-base font-bold text-neutral-900 dark:text-white truncate">
                {t('agent.createPlayer.title')}
              </div>
              <div className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
                {mode === 'agent'
                  ? t('agent.createPlayer.subtitle.agent')
                  : t('agent.createPlayer.subtitle.admin')}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('agent.createPlayer.close')}
            className="text-2xl leading-none text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
          >
            ×
          </button>
        </div>

        {/* Body — scrolls if content overflows */}
        <div className="overflow-y-auto px-5 py-4 space-y-3.5">
          {err && (
            <div className="px-3 py-2 rounded-lg bg-rose-50 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-400/30
                            text-rose-700 dark:text-rose-200 text-xs">
              {err}
            </div>
          )}

          <Section title={t('agent.createPlayer.section.identity.title')} hint={t('agent.createPlayer.section.identity.hint')}>
            <Row>
              <Field label={t('agent.createPlayer.field.username.label')} required>
                <input value={username} required minLength={3} maxLength={30}
                       onChange={(e) => setUsername(e.target.value.replace(/[^A-Za-z0-9_]/g, '').slice(0, 30))}
                       placeholder={t('agent.createPlayer.field.username.placeholder')} className={inp} />
              </Field>
              <Field label={t('agent.createPlayer.field.email.label')} required>
                <input type="email" value={email} required maxLength={120}
                       onChange={(e) => setEmail(e.target.value)} placeholder={t('agent.createPlayer.field.email.placeholder')} className={inp} />
              </Field>
            </Row>
            <Row>
              <Field label={t('agent.createPlayer.field.phone.label')} required hint={t('agent.createPlayer.field.phone.hint')}>
                <input value={phone} required inputMode="numeric" maxLength={15}
                       onChange={(e) => setPhone(e.target.value)} placeholder={t('agent.createPlayer.field.phone.placeholder')}
                       className={inp + ' tabular-nums tracking-wider'} />
              </Field>
              <Field label={t('agent.createPlayer.field.password.label')} required hint={t('agent.createPlayer.field.password.hint')}>
                <input type="text" value={password} required minLength={8} maxLength={72}
                       onChange={(e) => setPassword(e.target.value)} placeholder={t('agent.createPlayer.field.password.placeholder')}
                       className={inp + ' font-mono'} />
              </Field>
            </Row>
          </Section>

          {mode === 'admin' && (
            <Section title={t('agent.createPlayer.section.owner.title')} hint={t('agent.createPlayer.section.owner.hint')}>
              <Field label={t('agent.createPlayer.field.owner.label')}>
                <select value={agentId === '' ? '' : String(agentId)}
                        onChange={(e) => setAgentId(e.target.value === '' ? '' : Number(e.target.value))}
                        className={inp}>
                  <option value="">{t('agent.createPlayer.field.owner.none')}</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      @{a.username}{a.level != null ? ` (L${a.level})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </Section>
          )}

          <Section title={t('agent.createPlayer.section.profile.title')} hint={t('agent.createPlayer.section.profile.hint')}>
            <Row>
              <Field label={t('agent.createPlayer.field.firstName.label') + ' *'}>
                <input value={firstName} maxLength={80} required minLength={2}
                       onChange={(e) => setFirstName(e.target.value)} className={inp} />
              </Field>
              <Field label={t('agent.createPlayer.field.lastName.label') + ' *'}>
                <input value={lastName} maxLength={80} required minLength={2}
                       onChange={(e) => setLastName(e.target.value)} className={inp} />
              </Field>
            </Row>
            <Row>
              <Field label={t('agent.createPlayer.field.dob.label') + ' *'}>
                <input type="date" required value={birthdate} onChange={(e) => setBirthdate(e.target.value)} className={inp}
                  min="1900-01-01" max="2025-12-31" />
              </Field>
              <Field label={t('agent.createPlayer.field.gender.label')}>
                <select value={gender} onChange={(e) => setGender(e.target.value)} className={inp}>
                  {GENDER_OPTS.map((g) => <option key={g.value} value={g.value}>{t(g.labelKey)}</option>)}
                </select>
              </Field>
            </Row>
            <Field label={t('agent.createPlayer.field.language.label')}>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} className={inp}>
                {LANGUAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </Section>

          {/* Location — Phase R3 structured capture. Optional from the
              operator side: the player can fill this in themselves on
              first login, and the agent_id we pass below already
              gives the platform attribution. Useful when the operator
              KNOWS the player's pincode — it pre-stamps the geo
              fields so the player doesn't have to re-enter on day 1. */}
          <Section title={t('agent.createPlayer.section.location.title')} hint={t('agent.createPlayer.section.location.hint')}>
            {/*
              mode="child" is critical here. The operator (admin or agent)
              is filling in the PLAYER's location, not their own — so we
              hide the GPS button (capturing the creator's GPS would
              falsely attribute the new player to the creator's
              coordinates). The player can overlay precise GPS themselves
              on first login via the GeoForcedModal.
            */}
            <LocationCapture
              mode="child"
              value={geo}
              onChange={setGeo}
              disabled={busy}
            />
          </Section>

          {/* Betting limits — set the player's cricket caps at creation so nothing
              is forgotten. Optional; blank = no cap (global limits still apply). */}
          <PlayerLimitsFields value={limits} onChange={(patch) => setLimits((l) => ({ ...l, ...patch }))} disabled={busy} />

          <div className="rounded-lg bg-neutral-50 dark:bg-neutral-950/60 border border-neutral-200 dark:border-neutral-800
                          px-3 py-2 text-[11px] text-neutral-500 dark:text-neutral-400">
            <Icon name="info" size={12} className="inline align-middle mr-1" />
            {t('agent.createPlayer.disclaimer')}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-neutral-100 dark:border-neutral-800">
          <button onClick={onClose} disabled={busy}
                  className="px-3 py-2 text-xs uppercase tracking-wider hover:underline disabled:opacity-50">
            {t('agent.createPlayer.cta.cancel')}
          </button>
          <button onClick={submit} disabled={!valid || busy}
                  className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider
                             bg-emerald-600 text-white hover:bg-emerald-500
                             disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
            {busy
              ? <><Icon name="progress_activity" size={14} className="animate-spin" /> {t('agent.createPlayer.cta.creating')}</>
              : <><Icon name="person_add" size={14} /> {t('agent.createPlayer.cta.submit')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

const inp = 'w-full px-2.5 py-1.5 rounded-md text-sm bg-white dark:bg-neutral-950/60 ' +
            'border border-neutral-300 dark:border-neutral-700 ' +
            'text-neutral-900 dark:text-neutral-100 ' +
            'focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/60';

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          {title}
        </div>
        {hint && <div className="text-[10.5px] text-neutral-400 dark:text-neutral-500">{hint}</div>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{children}</div>;
}

function Field({ label, hint, required, children }:
  { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <div className="text-neutral-500 dark:text-neutral-400 mb-1 font-bold uppercase tracking-wider text-[10px]">
        {label} {required && <span className="text-rose-500">*</span>}
      </div>
      {children}
      {hint && <div className="text-[10px] text-neutral-500 dark:text-neutral-500 mt-0.5">{hint}</div>}
    </label>
  );
}
